import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, ArrowDownRight } from "lucide-react";
import { MaskLines, Reveal } from "../site/Reveal";
import { scrollToId } from "../site/SmoothScroll";

export const ProductHero = ({ r }) => (
  <section data-testid="product-hero" className="relative grain overflow-hidden pt-32 md:pt-40 pb-16" style={{ background: r.tint }}>
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <Link to="/#ranges" data-testid="product-back-link" className="inline-flex items-center gap-2 font-mono text-xs text-[var(--muted)] hover:text-[var(--forest)]"><ArrowLeft size={14} /> All ranges</Link>
        <p className="eyebrow mt-8" style={{ color: r.accent }}>{r.category} · {r.species}</p>
        <MaskLines testId="product-title" className="font-display text-6xl sm:text-8xl lg:text-[8.5rem] font-light tracking-[-0.04em] leading-[0.9] mt-5" lines={[<span key="n" style={{ color: r.accent }}>{r.name}</span>]} />
        <MaskLines as="p" delay={0.35} className="font-display italic text-2xl md:text-4xl text-[var(--forest)] mt-4" lines={[r.tagline]} />
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="mt-6 text-base md:text-lg text-[var(--muted)] max-w-xl leading-relaxed">{r.lead}</motion.p>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }} className="mt-9 flex flex-wrap gap-3">
          <button onClick={() => scrollToId("enquire")} data-testid="product-quote-button" className="inline-flex items-center gap-2 rounded-full px-7 py-4 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5" style={{ background: r.accent }}>
            Request a quote <ArrowDownRight size={18} />
          </button>
          <a href={r.brochure} download data-testid="product-brochure-button" className="inline-flex items-center gap-2 rounded-full border border-[var(--forest)] px-7 py-4 font-semibold text-[var(--forest)] transition-[background-color,color] duration-300 hover:bg-[var(--forest)] hover:text-[var(--bone)]">
            <Download size={16} /> Technical brochure
          </a>
        </motion.div>
      </div>
      <div className="lg:col-span-5 relative h-[440px] md:h-[560px]">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          animate={{ clipPath: "inset(0% 0 0 0)" }}
          transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
          className={`absolute right-0 top-0 h-[78%] w-[72%] arch overflow-hidden ${r.cutout ? "spotlight" : ""}`}
        >
          <img src={r.photo} alt={r.species} className={r.cutout ? "h-full w-full object-contain object-bottom mix-blend-multiply pt-10 px-4" : "h-full w-full object-cover"} />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 40, rotate: -4 }}
          animate={{ opacity: 1, y: 0, rotate: -3 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.7 }}
          className="absolute left-0 bottom-0 w-[46%] rounded-[22px] overflow-hidden border-4 border-[var(--paper)] shadow-[0_30px_60px_-30px_rgba(14,42,31,0.6)]"
        >
          <img src={r.pack} alt={`${r.name} pack`} className="w-full h-auto" />
        </motion.div>
      </div>
    </div>
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 border-t border-black/10 pt-8">
      {r.stats.map((s, i) => (
        <Reveal key={s.l} delay={i * 0.08} className="flex sm:block items-baseline gap-4">
          <p className="font-display text-5xl md:text-6xl font-light" style={{ color: r.accent }}>{s.v}</p>
          <p className="text-sm text-[var(--muted)] mt-1 max-w-[16rem]">{s.l}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

export const Benefits = ({ r }) => (
  <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28">
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--line)] rounded-[24px] overflow-hidden border border-[var(--line)]">
      {r.benefits.map((b, i) => (
        <Reveal key={b} delay={i * 0.06} className="bg-[var(--paper)] p-6 sm:p-8 min-h-[120px] sm:min-h-[180px] flex gap-4 flex-col justify-between">
          <span className="font-mono text-xs" style={{ color: r.accent }}>0{i + 1}</span>
          <p data-testid={`benefit-${i}`} className="font-display text-2xl text-[var(--forest)] leading-snug">{b}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

export const SpecTable = ({ r }) => (
  <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-10">
    <div className="lg:col-span-4">
      <p className="eyebrow" style={{ color: r.accent }}>Technical specifications</p>
      <h2 className="font-display text-4xl md:text-5xl font-light text-[var(--forest)] mt-4">At a glance</h2>
    </div>
    <dl data-testid="spec-table" className="lg:col-span-8 divide-y divide-[var(--line)] border-y border-[var(--line)]">
      {r.specs.map(([k, v]) => (
        <div key={k} className="grid sm:grid-cols-3 gap-2 py-5">
          <dt className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] pt-1">{k}</dt>
          <dd className="sm:col-span-2 text-[var(--ink)] text-base">{v}</dd>
        </div>
      ))}
    </dl>
  </section>
);

export const ChartCard = ({ tag, title, color, children, className = "" }) => (
  <Reveal className={`rounded-[24px] border border-[var(--line)] bg-[var(--paper)] p-6 md:p-8 ${className}`}>
    <p className="eyebrow text-[11px]" style={{ color }}>{tag}</p>
    <h3 className="font-display text-2xl text-[var(--forest)] mt-2 mb-6">{title}</h3>
    {children}
  </Reveal>
);
